import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserInfo } from '../types/auth';
import { authApi } from '../services/authApi';
import analytics from '../services/analytics';
import { initializeRevenueCat, logoutRevenueCat } from '../config/revenuecat';

interface AuthContextType {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  googleLogin: (idToken: string) => Promise<void>;
  register: (firstName: string, lastName: string, email: string, password: string, confirmPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<boolean>;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await authApi.refreshToken(refreshToken);
      if (response.success && response.accessToken && response.user) {
        localStorage.setItem('accessToken', response.accessToken);
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }
        setUser(response.user);
        // Identify user for advanced tracking after token refresh
        analytics.identifyUser(response.user.email, response.user.firstName, response.user.lastName, response.user.id);
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Clear invalid tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
    }
    
    return false;
  }, []);

  const checkCurrentUser = useCallback(async (token: string) => {
    try {
      const response = await authApi.getCurrentUser(token);
      if (response.success && response.user) {
        setUser(response.user);
        // Identify user for advanced tracking when app loads
        analytics.identifyUser(response.user.email, response.user.firstName, response.user.lastName, response.user.id);
        // Initialize RevenueCat with user ID and email (synchronous)
        try {
          console.log('Initializing RevenueCat on app load with user ID:', response.user.id);
          initializeRevenueCat(response.user.id, response.user.email);
          console.log('RevenueCat initialized for user:', response.user.id);
        } catch (error) {
          console.error('Failed to initialize RevenueCat on app load:', error);
        }
      } else {
        // Token might be expired, try to refresh
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      }
    } catch (error: any) {
      console.error('Failed to get current user:', error);
      
      // If token is expired (401), try to refresh
      if (error.status === 401) {
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      } else {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      }
    } finally {
      setIsLoading(false);
    }
  }, [refreshAccessToken]);

  useEffect(() => {
    // Check for stored token on app load
    const token = localStorage.getItem('accessToken');
    if (token) {
      checkCurrentUser(token);
    } else {
      setIsLoading(false);
    }
  }, [checkCurrentUser]);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await authApi.login({ email, password });
      
      if (response.success && response.accessToken && response.user) {
        localStorage.setItem('accessToken', response.accessToken);
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }
        setUser(response.user);
        // Track successful login (now automatically includes user identification)
        analytics.login('email', response.user.email, response.user.id, response.user.firstName, response.user.lastName);
        // Initialize RevenueCat with user ID and email (synchronous)
        try {
          console.log('Initializing RevenueCat on email login with user ID:', response.user.id);
          initializeRevenueCat(response.user.id, response.user.email);
          console.log('RevenueCat initialized for user on login:', response.user.id);
        } catch (error) {
          console.error('Failed to initialize RevenueCat on login:', error);
        }
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      setError(error.message || 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = async (idToken: string): Promise<void> => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await authApi.googleLogin({ idToken });
      
      if (response.success && response.accessToken && response.user) {
        localStorage.setItem('accessToken', response.accessToken);
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }
        setUser(response.user);
        // Track successful Google login (now automatically includes user identification)
        if(response.isNewUser) {
          analytics.register('google', response.user.email, response.user.firstName, response.user.lastName, response.user.id);
        }
        analytics.login('google', response.user.email, response.user.id, response.user.firstName, response.user.lastName);
        // Initialize RevenueCat with user ID and email (synchronous)
        try {
          console.log('Initializing RevenueCat on Google login with user ID:', response.user.id);
          initializeRevenueCat(response.user.id, response.user.email);
          console.log('RevenueCat initialized for user on Google login:', response.user.id);
        } catch (error) {
          console.error('Failed to initialize RevenueCat on Google login:', error);
        }
      } else {
        throw new Error(response.message || 'Google login failed');
      }
    } catch (error: any) {
      setError(error.message || 'Google login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    confirmPassword: string
  ): Promise<void> => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await authApi.register({
        firstName,
        lastName,
        email,
        password,
        confirmPassword,
      });
      
      if (response.success) {
        // Registration successful - don't auto-login, let user go to login page
        console.log('Registration successful for:', response.user?.email);
        // Track successful registration
        if (response.user) {
          analytics.register('email', response.user.email, response.user.firstName, response.user.lastName, response.user.id);
        }
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error: any) {
      setError(error.message || 'Registration failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    // Try to revoke the refresh token on the server
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      try {
        await authApi.revokeToken(refreshToken);
      } catch (error) {
        console.warn('Failed to revoke token on server:', error);
      }
    }
    
    // Track logout event
    analytics.logout();
    
    // Logout from RevenueCat (synchronous)
    try {
      logoutRevenueCat();
    } catch (error) {
      console.error('Failed to logout from RevenueCat:', error);
    }
    
    // Clear local storage and state
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setError(null);
    
    // Redirect to root path
    navigate('/');
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    googleLogin,
    register,
    logout,
    refreshAccessToken,
    error,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};